/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Data
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";

// Dashboard components
import { Card, Dialog, Icon, IconButton } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import ExperienceData from "../../tables/data/experienceTableData";
import EducationsData from "../../tables/data/educationTableData";
import Tags from "../components/Tags";
import MDAvatar from "components/MDAvatar";
import team2 from "assets/images/team-2.jpg";
import { useLocation } from "react-router-dom";
import profileIcon from "assets/images/profile.png";
import { useEffect, useState } from "react";
import { getProfile } from "api";
import moment from "moment";
import { Capitalize } from "utilities";
import MDButton from "components/MDButton";
import CloseIcon from "@mui/icons-material/Close";

function ProfileDetail() {
  const { sales, tasks } = reportsLineChartData;

  const location = useLocation();
  const { user } = location.state || {};
  const [profile, setProfile] = useState();

  const [pdfUrl, setPdfUrl] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);

  useEffect(() => {
    getProfileApi();
  }, []);

  const getProfileApi = () => {
    var params = {};

    getProfile({
      params: params,
      id: user?._id,
    })
      .then((responseJson) => {
        if (responseJson) {
          if (responseJson?.status === 400) {
          } else {
            // Keyboard.dismiss();
            setProfile(responseJson?.user);
          }
        }
      })
      .catch((e) => {});
  };

  const ExperienceAndEducation = () => {
    return (
      <MDBox px={3}>
        <ExperienceData
          experiences={profile?.experience || []}
          isMyProfile={false}
        ></ExperienceData>

        <EducationsData educations={profile?.education || []} isMyProfile={false}></EducationsData>
      </MDBox>
    );
  };

  const SkillsView = () => {
    return (
      <MDBox pt={3}>
        <MDTypography variant="h6">Skills</MDTypography>
        <Tags
          tags={profile?.skills || []}
          // onRemove={(tag) => console.log("Remove:", tag)}
          isMyProfile={false}
        />
      </MDBox>
    );
  };

  const LocationView = () => {
    return (
      <MDBox pt={3}>
        <MDTypography variant="h6">Preferred Locations</MDTypography>
        <Tags
          tags={profile?.locations || []}
          // onRemove={(tag) => console.log("Remove:", tag)}
          isMyProfile={false}
        />
      </MDBox>
    );
  };

  const IndustryView = () => {
    const industries = Array.isArray(profile?.industry)
      ? profile?.industry.map((i) => i?.name)
      : profile?.industry;

    return (
      <MDBox pt={3}>
        <MDTypography variant="h6">Industry</MDTypography>

        <Tags
          tags={industries || []}
          // onRemove={(tag) => console.log("Remove:", tag)}
          isMyProfile={false}
        />
      </MDBox>
    );
  };

  const JobTypeView = () => {
    const jobTypes = Array.isArray(profile?.jobType)
      ? profile?.jobType.map((i) => i?.name)
      : profile?.jobType;
    return (
      <MDBox pt={3}>
        <MDTypography variant="h6">Job Type</MDTypography>

        <Tags
          tags={jobTypes || []}
          // onRemove={(tag) => console.log("Remove:", tag)}
          isMyProfile={false}
        />
      </MDBox>
    );
  };

  const SkillAndJob = () => {
    return (
      <MDBox pl={3} pr={3}>
        {/* <MDTypography variant="h5" fontWeight="bold" mb={2}>
          Skills & Job Preferences
        </MDTypography> */}

        <SkillsView />
        <LocationView />
        <IndustryView />
        <JobTypeView />
      </MDBox>
    );
  };

  const LinkedinUrlView = () => {
    return (
      <MDBox p={3}>
        <MDTypography variant="h6">Linkedin URL</MDTypography>

        <MDTypography mb={0.25} display="block" variant="caption">
          {profile?.linkedinURL}
        </MDTypography>
      </MDBox>
    );
  };

  const handleResume = () => {
    if (profile?.documentURL) {
      setPdfUrl(profile?.documentURL);
      setOpenPreview(true);
    }
  };

  const ProfileDetailData = () => {
    const imgWidth = profile?.profileURL ? "100%" : "50%";
    const imgHeight = profile?.profileURL ? "100%" : "50%";
    const imgResize = profile?.profileURL ? "cover" : "contain";

    return (
      <MDBox
        p={3}
        display="flex"
        gap={2}
        flexDirection={{ xs: "column", sm: "row" }}
        flexWrap="wrap"
      >
        <MDBox display="flex" gap={2}>
          <MDAvatar
            src={profile?.profileURL || profileIcon}
            size="sm"
            sx={{
              width: "70px", // optional: control size
              height: "70px", // optional: control size
              border: "2px solid",
              borderColor: "success.main",
              "& img": {
                width: imgWidth,
                height: imgHeight,
                objectFit: imgResize,
                objectPosition: "center",
              },
            }}
          />
          <MDBox sx={{ width: { xs: "100%", sm: "200px" } }}>
            <MDTypography display="block" variant="h5" fontWeight="medium" mb={0.5}>
              {`${profile?.firstName || ""} ${profile?.lastName || ""}`}
            </MDTypography>

            <MDTypography mb={0.25} display="block" variant="caption">
              {`${profile?.code} ${profile?.phone}`}
            </MDTypography>
            <MDTypography mb={0.25} display="block" variant="caption">
              {`${profile?.email || ""}`}
            </MDTypography>

            <MDTypography display="block" variant="caption">
              {`${Capitalize(profile?.gender || "")} | ${moment(profile?.dob).format(
                "DD MMM YYYY"
              )}`}
            </MDTypography>
          </MDBox>
        </MDBox>
        {profile?.documentURL && (
          <MDBox
            sx={{
              ml: { xs: 0, sm: "auto" },
              mt: { xs: 2, sm: 0 },
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <MDButton variant="outlined" color="secondary" size="medium" onClick={handleResume}>
              <Icon fontSize="small">article_outlined</Icon>&nbsp; Resume
            </MDButton>
          </MDBox>
        )}
      </MDBox>
    );
  };

  const pdfViewer = () => {
    return (
      <Dialog open={openPreview} onClose={() => setOpenPreview(false)} fullWidth maxWidth="lg">
        <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6">PDF Preview</MDTypography>
          <IconButton onClick={() => setOpenPreview(false)}>
            <CloseIcon />
          </IconButton>
        </MDBox>
        <iframe
          src={`https://docs.google.com/gview?url=${encodeURIComponent(pdfUrl)}&embedded=true`}
          style={{ width: "100%", height: "80vh", border: "none" }}
          title="PDF Viewer"
        />
      </Dialog>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar title={"Profile"} />
      <MDBox py={3}>
        <Card>
          <ProfileDetailData />
          <ExperienceAndEducation />
          <SkillAndJob />
          <LinkedinUrlView />
        </Card>
      </MDBox>
      <MDBox mb={2} />
      <Footer />
      {pdfViewer()}
    </DashboardLayout>
  );
}

export default ProfileDetail;
